import { FormEvent, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';

const inputClass =
  'mt-2 w-full rounded-2xl border-2 border-slate-200 px-4 py-3.5 text-lg focus:border-teal-500 focus:outline-none focus:ring-4 focus:ring-teal-500/20';

export function SiteMasterProfilePage() {
  const { master, refresh } = useSiteMasterSession();
  const [displayName, setDisplayName] = useState(master?.display_name ?? '');
  const [curPass, setCurPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDisplayName(master?.display_name ?? '');
  }, [master?.display_name]);

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; detail?: string }>('site_master_update_profile', {
        method: 'POST',
        body: JSON.stringify({ display_name: displayName.trim() }),
      });
      if (r.ok) {
        setMsg('称呼已保存');
        await refresh();
      } else {
        setErr('保存失败');
      }
    } catch {
      setErr('保存失败');
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    if (newPass !== newPass2) {
      setErr('两次输入的新密码不一致');
      return;
    }
    if (newPass.length < 6) {
      setErr('新密码至少 6 位');
      return;
    }
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('site_master_change_password', {
        method: 'POST',
        body: JSON.stringify({ current_password: curPass, new_password: newPass }),
      });
      if (r.ok) {
        setMsg('密码已修改');
        setCurPass('');
        setNewPass('');
        setNewPass2('');
      } else {
        setErr(r.detail ?? (r.err === 'bad_current_password' ? '当前密码不正确' : '修改失败'));
      }
    } catch {
      setErr('修改失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      {msg ? <p className="rounded-2xl bg-emerald-50 px-4 py-3 text-lg text-emerald-900">{msg}</p> : null}
      {err ? <p className="rounded-2xl bg-red-50 px-4 py-3 text-lg text-red-900">{err}</p> : null}

      <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80">
        <h2 className="text-xl font-bold text-slate-900">我的账号</h2>
        <p className="mt-2 text-lg text-slate-600">
          手机号：<span className="font-mono font-semibold text-slate-800">{master?.phone}</span>
        </p>
        <form onSubmit={saveProfile} className="mt-4">
          <label className="block text-lg font-semibold text-slate-700">
            您的称呼（选填）
            <input
              className={inputClass}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="如：张师傅"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="mt-4 w-full rounded-2xl bg-slate-800 py-3.5 text-lg font-bold text-white disabled:opacity-60"
          >
            保存称呼
          </button>
        </form>
      </section>

      <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80">
        <h2 className="text-xl font-bold text-slate-900">修改密码</h2>
        <form onSubmit={changePassword} className="mt-4 space-y-4">
          <label className="block text-lg font-semibold text-slate-700">
            当前密码
            <input
              type="password"
              className={inputClass}
              value={curPass}
              onChange={(e) => setCurPass(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label className="block text-lg font-semibold text-slate-700">
            新密码
            <input
              type="password"
              className={inputClass}
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <label className="block text-lg font-semibold text-slate-700">
            再次输入新密码
            <input
              type="password"
              className={inputClass}
              value={newPass2}
              onChange={(e) => setNewPass2(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-2xl bg-teal-600 py-3.5 text-lg font-bold text-white disabled:opacity-60"
          >
            确认修改密码
          </button>
        </form>
      </section>
    </div>
  );
}
